/**
 * Shared expert-review visit context — status API, sticky entry `source`, first-status hub redirect.
 *
 * Belongs here: statusData, loading/error flags, entrySource, useExpertReviewStatusContext, the entry gate.
 * Does not belong here: questionnaire answers or leaving the feature.
 */
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import ThreeLineLoading from '../../../../components/ui-components/loading/ThreeLineLoading'
import { useExpertReviewNavigation } from '../navigation/expertReviewNavigation'
import { isExpertReviewHubPath } from '../navigation/expertReviewPaths'
import type { StatusQuestionnaireApiResponse } from '../types/statusQuestionnaire.types'
import type { QuestionnaireStatus } from '../shared/types/questionnaire.types'

/** Native screen that opened this webview — query `source`. */
export const ExpertReviewSource = {
  PORTFOLIO_DASHBOARD: 'PORTFOLIO_DASHBOARD',
  ELITE_LANDING_DASHBOARD: 'ELITE_LANDING_DASHBOARD',
  ALLOCATION_PLAN_PAGE: 'ALLOCATION_PLAN_PAGE'
} as const

export type ExpertReviewSource =
  (typeof ExpertReviewSource)[keyof typeof ExpertReviewSource]

export interface ExpertReviewStatusContextValue {
  statusData: StatusQuestionnaireApiResponse | undefined
  isStatusLoading: boolean
  isStatusError: boolean
  /** Sticky entry `source` for this visit; null when missing/unknown. */
  entrySource: ExpertReviewSource | null
}

/**
 * Status slice passed into strategy/preferences session hooks for FullFlow hydrate + loading.
 */
export type ExpertReviewStatus = {
  isStatusLoading: boolean
  isStatusError: boolean
  questionnaireStatus: QuestionnaireStatus | null | undefined
}

export const ExpertReviewStatusContext = createContext<ExpertReviewStatusContextValue | undefined>(
  undefined
)

export function useExpertReviewStatusContext() {
  const context = useContext(ExpertReviewStatusContext)
  if (!context) {
    throw new Error('useExpertReviewStatusContext must be used within an ExpertReviewProviders')
  }
  return context
}

/**
 * Epoch ms from status. Null when the field is missing or not a finite number.
 * Cold submitted hub uses this. The pending-visit countdown stays on POST /submit.
 */
export function firstPlanDropTimeFromStatus(
  statusData: StatusQuestionnaireApiResponse | undefined
): number | null {
  const dropTime = statusData?.firstPlanDropTime
  if (typeof dropTime !== 'number' || !Number.isFinite(dropTime)) return null
  return dropTime
}

/**
 * First settled /status for this visit only. A finite firstPlanDropTime replaces
 * the opened route with the hub. Later refetches do not redirect.
 * Children stay unmounted until that first result, so the opened page cannot flash.
 */
export function ExpertReviewInitialStatusGate({ children }: { children: ReactNode }) {
  const { statusData, isStatusLoading, isStatusError } = useExpertReviewStatusContext()
  const { pathname } = useLocation()
  const { goToHub } = useExpertReviewNavigation()
  // undefined = first status has not settled. null = settled with no epoch.
  const [entryDropTime, setEntryDropTime] = useState<number | null | undefined>(undefined)
  // Stays open after the first decision so a later screen in this visit is not pulled back.
  const [hasPassedInitialStatusGate, setHasPassedInitialStatusGate] = useState(false)
  const didRedirectRef = useRef(false)
  const goToHubRef = useRef(goToHub)
  goToHubRef.current = goToHub
  const onHub = isExpertReviewHubPath(pathname)

  useEffect(() => {
    if (entryDropTime !== undefined) return
    if (isStatusLoading) return
    // Failed first request: show the opened route. A later success is not this check.
    if (isStatusError) {
      setEntryDropTime(null)
      return
    }
    setEntryDropTime(firstPlanDropTimeFromStatus(statusData))
  }, [entryDropTime, isStatusLoading, isStatusError, statusData])

  useEffect(() => {
    if (hasPassedInitialStatusGate) return
    if (entryDropTime === undefined) return
    // Still replacing a non-hub URL that opened with an epoch.
    if (entryDropTime !== null && !onHub) return
    setHasPassedInitialStatusGate(true)
  }, [hasPassedInitialStatusGate, entryDropTime, onHub])

  useEffect(() => {
    if (didRedirectRef.current) return
    if (entryDropTime == null) return
    if (onHub) return
    didRedirectRef.current = true
    // Hub root drops inner expert-review frames, so Back cannot return to this URL.
    goToHubRef.current({ replace: true })
  }, [entryDropTime, onHub])

  // Hub renders through its own status wait. Other routes wait so they cannot flash.
  if (!hasPassedInitialStatusGate && !onHub) {
    return <ThreeLineLoading />
  }

  return children
}
